import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useApi } from '@/hooks/useAppApi'
import { useAuth } from '@/hooks/useAppAuth'
import { hasPermission, hasAnyPermission, hasAllPermissions } from '@shared/auth/permissions'

interface ProfileResponse {
  permissions: string[]
  scopes: string[]
  roles: { name: string; permissions: string[] }[]
}

interface UsePermissionsReturn {
  permissions: string[]
  /**
   * Role names only, without the flattened permission strings that
   * `permissions` also contains. Used for display and for analytics identity
   * (Pendo visitor metadata) where "admin" is meaningful but
   * "admin:users:read" is noise.
   */
  roleNames: string[]
  isLoading: boolean
  hasPermission: (required: string) => boolean
  hasAnyPermission: (required: string[]) => boolean
  hasAllPermissions: (required: string[]) => boolean
}

export function usePermissions(): UsePermissionsReturn {
  const api = useApi()
  const { isAuthenticated } = useAuth()
  const { user, isInitializing } = api

  const profileQuery = useQuery<ProfileResponse>({
    queryKey: ['profile', 'permissions'],
    queryFn: () => api.get('/profile') as Promise<ProfileResponse>,
    enabled: isAuthenticated && !isInitializing,
    staleTime: 5 * 60 * 1000,
  })
  const profile = profileQuery.data

  const permissions = useMemo(() => {
    const jwtScopes = user?.scopes ?? []
    const dbPermissions = profile?.permissions ?? []
    const roleNames = profile?.roles?.map((r) => r.name) ?? []
    const combined = new Set([...jwtScopes, ...dbPermissions, ...roleNames])
    return Array.from(combined)
  }, [user, profile])

  // Surface the profile query's pending state so ProtectedRoute waits for
  // DB-backed permissions (e.g. role-resolved `*:*`) before deciding whether
  // to redirect to /unauthorized. Without this, the route would render with
  // only JWT scopes and deny access while the profile is still in flight.
  const isProfilePending =
    isAuthenticated && !isInitializing && profileQuery.fetchStatus === 'fetching' && !profile

  const roleNames = useMemo(() => profile?.roles?.map((r) => r.name) ?? [], [profile])

  return {
    permissions,
    roleNames,
    isLoading: isInitializing || isProfilePending,
    hasPermission: (required: string): boolean => hasPermission(permissions, required),
    hasAnyPermission: (required: string[]): boolean => hasAnyPermission(permissions, required),
    hasAllPermissions: (required: string[]): boolean => hasAllPermissions(permissions, required),
  }
}
